import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Award, Download, Search, Check, Plus, Trash2, ListChecks, Mic, Save, FolderOpen, Camera, X as XIcon, Upload as UploadIcon, ImageIcon } from "lucide-react";
import PhotoCropperDialog from "@/components/PhotoCropperDialog";

export default function BulkCertificatePrintDialog({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const today = new Date().toLocaleDateString("ar", { year: "numeric", month: "long", day: "numeric" });
  const [coverInclude, setCoverInclude] = useState(true);
  const [coverTitle, setCoverTitle] = useState("حفل تخريج شهادات الإنجاز");
  const [coverSubtitle, setCoverSubtitle] = useState("مركز Happy Day للاحتياجات الخاصة");
  const [coverDate, setCoverDate] = useState(today);
  const [programInclude, setProgramInclude] = useState(false);
  const [directorSpeech, setDirectorSpeech] = useState(
    "بسم الله الرحمن الرحيم، أرحّب بأولياء الأمور والضيوف الكرام في حفلنا اليوم..."
  );
  const [schedule, setSchedule] = useState([
    { time: "5:00 م", title: "استقبال الضيوف والتسجيل", speaker: "لجنة الاستقبال", speaker_photo: "" },
    { time: "5:15 م", title: "تلاوة عطرة من القرآن الكريم", speaker: "أ. أحمد", speaker_photo: "" },
    { time: "5:25 م", title: "كلمة مدير المركز", speaker: "أ. محمد عاطف", speaker_photo: "" },
    { time: "5:45 م", title: "توزيع شهادات الإنجاز", speaker: "الإدارة", speaker_photo: "" },
    { time: "6:30 م", title: "تصوير جماعي وضيافة", speaker: "", speaker_photo: "" },
  ]);
  const [templates, setTemplates] = useState([]);
  const [savingTpl, setSavingTpl] = useState(false);
  const [cropTarget, setCropTarget] = useState(null); // {index, sourceUrl}
  const [programBanner, setProgramBanner] = useState(""); // decorative banner data URL

  const uploadBanner = (file) => {
    if (!file) return;
    if (file.size > 800_000) { toast.error("حجم اللافتة أكبر من 800KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setProgramBanner(reader.result);
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelected(new Set());
    api.get("/admin/eligible-mastery-certificates")
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(extractError(e)))
      .finally(() => setLoading(false));
    api.get("/admin/program-templates")
      .then((r) => setTemplates(r.data || []))
      .catch(() => { /* non-critical */ });
  }, [open]);

  const loadTemplate = (tpl) => {
    if (!tpl) return;
    setSchedule((tpl.schedule || []).map((r) => ({
      time: r.time || "",
      title: r.title || "",
      speaker: r.speaker || "",
      speaker_photo: r.speaker_photo || "",
    })));
    setDirectorSpeech(tpl.director_speech || "");
    setProgramInclude(true);
    toast.success(`تم تحميل القالب: ${tpl.name}`);
  };

  const saveTemplate = async () => {
    const name = window.prompt("اسم القالب (مثال: حفل نهاية العام):", "");
    if (!name || !name.trim()) return;
    setSavingTpl(true);
    try {
      const { data } = await api.post("/admin/program-templates", {
        name: name.trim(),
        director_speech: directorSpeech,
        schedule,
      });
      toast.success(`💾 حُفظ القالب "${data.name}"`);
      const r = await api.get("/admin/program-templates");
      setTemplates(r.data || []);
    } catch (e) { toast.error(extractError(e)); }
    finally { setSavingTpl(false); }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`حذف قالب "${tpl.name}"؟`)) return;
    try {
      await api.delete(`/admin/program-templates/${tpl.id}`);
      setTemplates((prev) => prev.filter((x) => x.id !== tpl.id));
      toast.success("تم حذف القالب");
    } catch (e) { toast.error(extractError(e)); }
  };

  const setSpeakerPhoto = (idx, file) => {
    if (!file) return;
    if (file.size > 5_000_000) { toast.error("حجم الصورة أكبر من 5MB"); return; }
    // Open cropper with a blob URL so the admin can frame the face as a perfect circle
    const url = URL.createObjectURL(file);
    setCropTarget({ index: idx, sourceUrl: url });
  };

  const exportTemplate = (tpl) => {
    // Strip base64 photos from export (they can be re-uploaded per ceremony) to keep JSON small & shareable
    const payload = {
      name: tpl.name,
      director_speech: tpl.director_speech || "",
      schedule: (tpl.schedule || []).map((r) => ({ time: r.time || "", title: r.title || "", speaker: r.speaker || "" })),
      _exported_at: new Date().toISOString(),
      _source: "happy-day-ceremony-v1",
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `program-template-${tpl.name.replace(/\s+/g, "-")}.json`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("تم تصدير القالب");
  };

  const importTemplate = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed || typeof parsed !== "object") throw new Error("ملف غير صالح");
      const name = String(parsed.name || "").trim();
      if (!name) throw new Error("القالب بدون اسم");
      const sched = Array.isArray(parsed.schedule) ? parsed.schedule : [];
      const { data } = await api.post("/admin/program-templates", {
        name,
        director_speech: String(parsed.director_speech || ""),
        schedule: sched.map((r) => ({
          time: String(r.time || ""),
          title: String(r.title || ""),
          speaker: String(r.speaker || ""),
        })),
      });
      toast.success(`📥 استُورد القالب "${data.name}"`);
      const r = await api.get("/admin/program-templates");
      setTemplates(r.data || []);
    } catch (e) {
      toast.error(`فشل الاستيراد: ${e.message || extractError(e)}`);
    }
  };

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((it) => (it.child_name || "").toLowerCase().includes(t) || (it.domain_name || "").toLowerCase().includes(t));
  }, [q, items]);

  const keyOf = (it) => `${it.child_id}::${it.domain_id}`;
  const toggle = (it) => setSelected((s) => {
    const n = new Set(s);
    const k = keyOf(it);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  const generate = async () => {
    if (selected.size === 0) { toast.error("اختر شهادة واحدة على الأقل"); return; }
    setPrinting(true);
    try {
      const payload = {
        items: items.filter((it) => selected.has(keyOf(it))).map((it) => ({ child_id: it.child_id, domain_id: it.domain_id })),
        cover_include: coverInclude,
        cover_title: coverTitle,
        cover_subtitle: coverSubtitle,
        cover_event_date: coverDate,
        program_include: programInclude,
        program_director_speech: directorSpeech,
        program_schedule: schedule.filter((r) => (r.time || r.title || r.speaker || "").trim()),
        program_banner: programBanner,
      };
      const r = await api.post("/admin/bulk-mastery-certificates", payload, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `graduation-ceremony-${selected.size}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`🎓 تم إنشاء ملف يحوي ${selected.size} شهادة${coverInclude ? " + غلاف" : ""}${programInclude ? " + برنامج" : ""}`);
      onClose();
    } catch (e) { toast.error(extractError(e)); }
    finally { setPrinting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !printing) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bulk-cert-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black flex items-center gap-2">
            <Award className="w-6 h-6 text-amber-600" /> طباعة شهادات جماعية (حفل التخرج)
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500 mb-3">تظهر هنا كل الأزواج (طفل + مجال) التي بلغت 90%+ في أحدث تقييم. حدّد ما تريد طباعته في ملف PDF واحد.</div>

        {/* Cover page settings */}
        <div className="rounded-2xl border-2 border-amber-200 bg-amber-50/40 p-3 mb-3" data-testid="cover-page-settings">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={coverInclude} onChange={(e) => setCoverInclude(e.target.checked)} className="w-4 h-4 accent-amber-600" data-testid="cover-include-toggle" />
            <span className="font-black text-amber-900 text-sm">🎓 أضف صفحة غلاف (لوحة الشرف) في أول ملف PDF</span>
          </label>
          {coverInclude && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3">
              <div>
                <label className="text-[10px] font-black text-amber-800 mb-1 block">عنوان الحفل</label>
                <Input value={coverTitle} onChange={(e) => setCoverTitle(e.target.value)} className="h-9 text-sm" placeholder="حفل تخريج..." data-testid="cover-title-input" />
              </div>
              <div>
                <label className="text-[10px] font-black text-amber-800 mb-1 block">العنوان الفرعي (المركز)</label>
                <Input value={coverSubtitle} onChange={(e) => setCoverSubtitle(e.target.value)} className="h-9 text-sm" data-testid="cover-subtitle-input" />
              </div>
              <div>
                <label className="text-[10px] font-black text-amber-800 mb-1 block">تاريخ الحفل</label>
                <Input value={coverDate} onChange={(e) => setCoverDate(e.target.value)} className="h-9 text-sm" data-testid="cover-date-input" />
              </div>
              <div className="md:col-span-3 text-[10px] text-amber-700">
                💡 الشعار يُقتبس تلقائياً من شعار المركز في إعدادات صفحة الترحيب. قائمة المكرَّمين تُدرج تلقائياً على الغلاف (حتى 12 اسماً، والباقي كملخّص).
              </div>
            </div>
          )}
        </div>

        {/* Ceremony Program (schedule + speech) */}
        <div className="rounded-2xl border-2 border-orange-200 bg-orange-50/40 p-3 mb-3" data-testid="program-settings">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={programInclude} onChange={(e) => setProgramInclude(e.target.checked)} className="w-4 h-4 accent-orange-600" data-testid="program-include-toggle" />
            <span className="font-black text-orange-900 text-sm flex items-center gap-2"><ListChecks className="w-4 h-4" /> 📖 أضف كتيّب البرنامج (جدول الحفل + كلمة المدير)</span>
          </label>
          {programInclude && (
            <div className="mt-3 space-y-3">
              {/* Templates row */}
              <div className="flex items-center gap-2 flex-wrap p-2 rounded-xl bg-white/70 border border-orange-200">
                <FolderOpen className="w-4 h-4 text-orange-700" />
                <span className="text-[11px] font-black text-orange-800">قوالب محفوظة:</span>
                {templates.length === 0 && (
                  <span className="text-[11px] text-orange-500">لا توجد قوالب — احفظ الأول للاستخدام لاحقاً</span>
                )}
                <div className="flex-1 flex items-center gap-1 flex-wrap" data-testid="program-templates-list">
                  {templates.map((tpl) => (
                    <span key={tpl.id} className="inline-flex items-center gap-1 bg-orange-100 text-orange-900 rounded-full pl-1 pr-2 py-0.5 text-[11px] font-bold" data-testid={`program-template-${tpl.id}`}>
                      <button
                        type="button"
                        onClick={() => loadTemplate(tpl)}
                        className="px-1.5 py-0.5 rounded-full hover:bg-orange-200"
                        title="تحميل هذا القالب"
                        data-testid={`program-template-load-${tpl.id}`}
                      >
                        📋 {tpl.name}
                      </button>
                      <button
                        type="button"
                        onClick={() => exportTemplate(tpl)}
                        className="text-orange-700 hover:bg-orange-200 rounded-full p-0.5"
                        title="تصدير القالب كملف JSON"
                        data-testid={`program-template-export-${tpl.id}`}
                      >
                        <Download className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteTemplate(tpl)}
                        className="text-rose-600 hover:bg-rose-100 rounded-full p-0.5"
                        title="حذف"
                        data-testid={`program-template-delete-${tpl.id}`}
                      >
                        <XIcon className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <Button
                  size="sm"
                  onClick={saveTemplate}
                  disabled={savingTpl}
                  className="h-7 gap-1 text-[11px] bg-orange-600 hover:bg-orange-700"
                  data-testid="program-save-template"
                >
                  <Save className="w-3 h-3" /> حفظ كقالب
                </Button>
                <label
                  className="cursor-pointer h-7 inline-flex items-center gap-1 text-[11px] px-2.5 rounded-md border border-orange-300 bg-white text-orange-700 hover:bg-orange-50"
                  data-testid="program-import-template-label"
                  title="استيراد قالب من ملف JSON"
                >
                  <UploadIcon className="w-3 h-3" /> استيراد
                  <input
                    type="file"
                    accept="application/json,.json"
                    className="hidden"
                    onChange={(e) => { importTemplate(e.target.files?.[0]); e.target.value = ""; }}
                    data-testid="program-import-template-input"
                  />
                </label>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] font-black text-orange-800">جدول الحفل ({schedule.length} فقرة)</label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSchedule((s) => [...s, { time: "", title: "", speaker: "", speaker_photo: "" }])}
                    className="h-7 gap-1 text-[11px] text-orange-700 border-orange-300"
                    data-testid="program-add-row"
                  >
                    <Plus className="w-3 h-3" /> إضافة فقرة
                  </Button>
                </div>
                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                  {schedule.map((row, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`program-row-${idx}`}>
                      <Input
                        value={row.time}
                        onChange={(e) => setSchedule((s) => s.map((r, i) => i === idx ? { ...r, time: e.target.value } : r))}
                        placeholder="5:00 م"
                        className="col-span-2 h-8 text-xs text-center"
                        data-testid={`program-row-${idx}-time`}
                      />
                      <Input
                        value={row.title}
                        onChange={(e) => setSchedule((s) => s.map((r, i) => i === idx ? { ...r, title: e.target.value } : r))}
                        placeholder="اسم الفقرة"
                        className="col-span-4 h-8 text-xs"
                        data-testid={`program-row-${idx}-title`}
                      />
                      <div className="col-span-1 flex justify-center">
                        <label
                          className="relative w-8 h-8 rounded-full border-2 border-orange-300 bg-white flex items-center justify-center cursor-pointer hover:border-orange-500 overflow-hidden group"
                          data-testid={`program-row-${idx}-photo-btn`}
                          title={row.speaker_photo ? "استبدل الصورة" : "أضف صورة المتحدّث"}
                        >
                          {row.speaker_photo ? (
                            <img src={row.speaker_photo} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Camera className="w-3.5 h-3.5 text-orange-600" />
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => { setSpeakerPhoto(idx, e.target.files?.[0]); e.target.value = ""; }}
                            data-testid={`program-row-${idx}-photo`}
                          />
                          {row.speaker_photo && (
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); setSchedule((s) => s.map((r, i) => i === idx ? { ...r, speaker_photo: "" } : r)); }}
                              className="absolute -top-1 -right-1 w-4 h-4 bg-rose-600 text-white rounded-full text-[8px] font-bold opacity-0 group-hover:opacity-100 hover:bg-rose-700 flex items-center justify-center"
                              title="إزالة الصورة"
                            >
                              ✕
                            </button>
                          )}
                        </label>
                      </div>
                      <Input
                        value={row.speaker}
                        onChange={(e) => setSchedule((s) => s.map((r, i) => i === idx ? { ...r, speaker: e.target.value } : r))}
                        placeholder="اسم المتحدّث"
                        className="col-span-4 h-8 text-xs"
                        data-testid={`program-row-${idx}-speaker`}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSchedule((s) => s.filter((_, i) => i !== idx))}
                        className="col-span-1 h-8 px-2 text-rose-600 border-rose-200"
                        data-testid={`program-row-${idx}-remove`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] font-black text-orange-800 mb-1 flex items-center gap-1"><Mic className="w-3 h-3" /> كلمة مدير المركز</label>
                <Textarea
                  dir="rtl"
                  rows={4}
                  value={directorSpeech}
                  onChange={(e) => setDirectorSpeech(e.target.value)}
                  placeholder="اكتب كلمة المدير هنا... (ستُطبَع كصفحة مستقلة داخل الكتيّب)"
                  className="text-xs"
                  data-testid="program-speech-input"
                />
                <div className="text-[10px] text-orange-700 mt-1">
                  الحد الأقصى: 3000 حرف. الأسطر الفارغة تصبح فواصل فقرات.
                </div>
              </div>
              <div className="rounded-xl border border-orange-200 bg-white/70 p-2" data-testid="program-banner-section">
                <label className="text-[11px] font-black text-orange-800 mb-1 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> لافتة تزيينية لأعلى صفحة الجدول (اختياري)</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {programBanner ? (
                    <img src={programBanner} alt="" className="h-14 rounded-lg border border-orange-200 object-contain bg-white" data-testid="program-banner-preview" />
                  ) : (
                    <div className="h-14 w-32 rounded-lg border border-dashed border-orange-300 text-[10px] text-orange-500 flex items-center justify-center">بدون لافتة</div>
                  )}
                  <label className="cursor-pointer inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-md border border-orange-300 bg-white text-orange-700 hover:bg-orange-50" data-testid="program-banner-upload-label">
                    <UploadIcon className="w-3 h-3" /> {programBanner ? "استبدال" : "رفع صورة"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => { uploadBanner(e.target.files?.[0]); e.target.value = ""; }}
                      data-testid="program-banner-upload"
                    />
                  </label>
                  {programBanner && (
                    <Button size="sm" variant="outline" onClick={() => setProgramBanner("")} className="h-7 text-[11px] text-rose-600 border-rose-200" data-testid="program-banner-remove">
                      <XIcon className="w-3 h-3" /> إزالة
                    </Button>
                  )}
                </div>
                <div className="text-[10px] text-orange-600 mt-1">صورة أفقية مثالية (مثل clip-art خريجين). الحد الأقصى 800KB. تظهر أعلى صفحة الجدول في PDF.</div>
              </div>
            </div>
          )}
        </div>

        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input dir="rtl" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث باسم الطفل أو المجال..." className="pr-9" data-testid="bulk-cert-search" />
        </div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-black text-amber-700 bg-amber-50 rounded-full px-3 py-1">
            محدّد: {selected.size} من {items.length} مؤهّل
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set(filtered.map(keyOf)))} data-testid="bulk-cert-select-all">تحديد المرئي</Button>
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} data-testid="bulk-cert-clear">مسح</Button>
          </div>
        </div>
        <div className="space-y-1.5 max-h-[45vh] overflow-y-auto pr-1">
          {loading ? (
            <div className="text-center py-10 text-slate-400 text-sm">جاري التحميل...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-14 text-slate-400 text-sm bg-slate-50 rounded-2xl">
              <Award className="w-10 h-10 mx-auto mb-2 opacity-40" />
              لا توجد شهادات مؤهّلة حالياً — راجع التقييمات وأكمل المهارات حتى يبلغ المجال 90%.
            </div>
          ) : filtered.map((it) => {
            const on = selected.has(keyOf(it));
            return (
              <label
                key={keyOf(it)}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${on ? "border-amber-300 bg-amber-50/60" : "border-slate-200 bg-white hover:border-amber-200"}`}
                data-testid={`bulk-cert-item-${keyOf(it)}`}
              >
                <input type="checkbox" checked={on} onChange={() => toggle(it)} className="w-4 h-4 accent-amber-600" />
                <div className="flex-1 min-w-0">
                  <div className="font-black text-slate-900 truncate">{it.child_name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    <span className="inline-block px-2 py-0.5 rounded-full text-white text-[10px] font-black" style={{ background: it.domain_color }}>{it.domain_name}</span>
                    <span className="mx-2">{it.achieved}/{it.total} مهارات</span>
                  </div>
                </div>
                <div className="text-lg font-black" style={{ color: it.domain_color }}>{it.percent}%</div>
                {on && <Check className="w-4 h-4 text-amber-600" />}
              </label>
            );
          })}
        </div>
        <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-slate-100">
          <Button variant="outline" onClick={onClose} disabled={printing}>إلغاء</Button>
          <Button
            onClick={generate}
            disabled={printing || selected.size === 0}
            className="gap-2 bg-gradient-to-l from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700"
            data-testid="bulk-cert-generate-btn"
          >
            <Download className="w-4 h-4" /> {printing ? "جارٍ إنشاء PDF..." : `إنشاء PDF مدمج (${selected.size})`}
          </Button>
        </div>

        <PhotoCropperDialog
          open={!!cropTarget}
          onClose={() => {
            if (cropTarget?.sourceUrl) URL.revokeObjectURL(cropTarget.sourceUrl);
            setCropTarget(null);
          }}
          imageUrl={cropTarget?.sourceUrl}
          onCropped={(dataUrl) => {
            const idx = cropTarget?.index;
            if (idx == null) return;
            setSchedule((s) => s.map((r, i) => i === idx ? { ...r, speaker_photo: dataUrl } : r));
            toast.success("تم قص صورة المتحدّث");
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
